// Arabic PDF font loader + smart text helper.
// Loads Amiri (SIL OFL) once and provides pdfText() that auto-detects Arabic
// characters in the string and switches to Amiri to render them correctly.
// Also right-aligns Arabic text automatically.

let _amiriBase64 = null;
let _loading = null;
let _warnedMissing = false;
// Track whether a jsPDF instance has already registered the font (WeakSet).
const _registered = new WeakSet();

// Match any Arabic block: Arabic (0600-06FF), Arabic Supplement (0750-077F),
// Arabic Extended-A (08A0-08FF), Arabic Presentation Forms-A (FB50-FDFF),
// Arabic Presentation Forms-B (FE70-FEFF).
const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

export function hasArabic(str) {
  if (str == null) return false;
  return ARABIC_RE.test(String(str));
}

async function fetchAmiri() {
  if (_amiriBase64) return _amiriBase64;
  if (_loading) return _loading;
  _loading = (async () => {
    const ttfUrl = "https://cdn.jsdelivr.net/gh/aliftype/amiri@1.001/fonts/Amiri-Regular.ttf";
    try {
      const resp = await fetch(ttfUrl, { cache: "force-cache" });
      if (!resp.ok) throw new Error("font fetch failed " + resp.status);
      const buf = await resp.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.byteLength; i += chunk) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
      }
      _amiriBase64 = btoa(bin);
      return _amiriBase64;
    } catch (e) {
      _loading = null;
      throw e;
    }
  })();
  return _loading;
}

/**
 * Registers Amiri on the given jsPDF instance if not already registered.
 * Returns true if font is available, false otherwise.
 */
export async function ensureArabicFont(doc) {
  try {
    if (_registered.has(doc)) return true;
    const b64 = await fetchAmiri();
    doc.addFileToVFS("Amiri-Regular.ttf", b64);
    doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
    _registered.add(doc);
    return true;
  } catch (e) {
    console.warn("ensureArabicFont failed, Arabic will render as squares:", e);
    return false;
  }
}

/**
 * Smart text renderer. Automatically switches to Amiri when the string
 * contains Arabic characters. When Arabic, defaults align to 'right' if
 * caller passes no align option.
 *
 * Prerequisite: ensureArabicFont(doc) has been called (best done once per
 * PDF, non-blocking on failure — text still renders in default helvetica).
 */
export function pdfText(doc, text, x, y, opts = {}) {
  const s = text == null ? "" : String(text);
  const isArabic = hasArabic(s);
  const prevFont = doc.getFont();
  try {
    if (isArabic) {
      if (_registered.has(doc)) {
        doc.setFont("Amiri", "normal");
      } else if (!_warnedMissing) {
        _warnedMissing = true;
        console.warn("pdfText: Arabic text seen but Amiri not registered; call ensureArabicFont(doc) before pdfText.");
      }
    }
    doc.text(s, x, y, opts);
  } finally {
    if (isArabic && prevFont && prevFont.fontName) {
      try { doc.setFont(prevFont.fontName, prevFont.fontStyle || "normal"); } catch (e) { console.warn("pdfFonts: failed to restore font", e); }
    }
  }
}
